import React, { useState } from 'react';
import { string, array, oneOfType, object, any } from 'prop-types';
import { map, isEmpty, get, size } from 'lodash';
import styled, { css } from 'styled-components';
import { Text, IconContainer, Icon, Column, Row, If } from '..';
import { CHINESE_SILVER, GAINSBORO, PLATINUM, WHITE, GREEN_DARK, BLACK } from '../../../utils/colors';

const CardTableWrapper = styled.div`
  height: 100%;
  display: flex;
  width: 100%;
  ${({ isMobile }) => isMobile && css`
    max-height: calc(100vh - 198px);
    th {
        position: sticky;
        top: 0;
        background-color: #ffffff;
        z-index: 1;
    }
  `}
`;

const ColumnWrapper = ({ parent, child }) => (
    <Column height='100%'>
        <Row flex={1} align='center' padding={10}>
            { parent }
        </Row>
        <If
            condition={child}
            then={
                <Row noFlex padding={10} height={43} borderTop={`1px solid ${PLATINUM}`}>
                    { child }
                </Row>
            }
        />
    </Column>
);

ColumnWrapper.propTypes = {
    parent: any,
    child: any
};

ColumnWrapper.defaultProps = {
    parent: null,
    child: null
};

// columns prop should be as follow
// This props can be growing
/*
[
    {
        index: 'index',// Required
        title: 'Title',// Required
        width: '100px',// Optional. Define custom column width
        sort: true,// Optional. Make this column sortable
        renderHeader: () => <span>Hello</span>,// Optional. If inst defined, title will be rendered
        headerStyles: { backgroundColor: '#FFFFFF' },// Optional. Set custom styles for this column
        render: item => <span>{ item }</span>,// Required. Elements to be rendered on this <td> column
        child: item => <span>{ item }</span>,// Optional. If defined, children column will be rendered
        columnStyle: { backgroundColor: '#FFFFFF' },// Optional. Set custom styles for this <td> Column
    }
]
*/

// TODO
/*
    Receive custom callback via props to handle data sorting
    Add pagination feature
    Mobile design
    Loading
 */

const BOTable = ({
    mode,
    data,
    isMobile,
    columns,
    tableStyles,
    pagination,
    ...props
}) => {
    const [sorted, setSorted] = useState(null);

    const sortHandler = (index) => {
        // If sorted hook has value, should be some like this ---> { index: 'name', order: 'desc' }
        if (!isEmpty(sorted) && (get(sorted, 'index') === index)) {
            switch (get(sorted, 'order')) {
                case 'desc':
                    return setSorted({ index, order: 'asc' });
                case 'asc':
                    return setSorted(null);
            };
        }
        return setSorted({ index, order: 'desc' });
    };

    const borderRadiusResolver = (index) => { // Lets round borders for the first and last column
        const lenght = size(columns);
        if (index === 0) return '8px 0 0 8px';
        if (index === (lenght - 1)) return '0 8px 8px 0';
        return 0;
    };

    const isCardMode = mode === 'card';

    const tableModeStyles = (node, aditionalParams) => { // Styles by card mode are defined here
        switch (node) {
            case 'table':
                return isCardMode ? {
                    borderCollapse: 'separate',
                    borderSpacing: '0 8px'
                } : {};
            case 'tr':
                return isCardMode ? {
                    boxShadow: 'rgb(0 0 0 / 15%) 0px 2px 4px',
                    borderRadius: '8px'
                } : {};
            case 'td':
                return isCardMode ? {
                    borderRadius: borderRadiusResolver(aditionalParams),
                } : {
                    borderBottom: `1px solid ${CHINESE_SILVER}`
                };
            default:
                break;
        }
    };

    return (
        <CardTableWrapper isMobile={isMobile}>
            <If
                condition={isEmpty(data)}
                then={
                    <Column align='center' justify='center' mB={20}>
                        <IconContainer margin={20} color={WHITE} border={`2px solid ${GREEN_DARK}`} size={30}>
                            <Icon name='exclamation' size={15} color={GREEN_DARK} />
                        </IconContainer>
                        <Text size={17} color={GREEN_DARK} text='No se han encontrado productos.' semibold />
                    </Column>
                }
                else={
                    <table
                        style={{
                            width: '100%',
                            ...tableModeStyles('table'),
                            ...tableStyles
                        }}
                    >
                        <thead>
                            <tr>
                                { map(columns, ({ title, index, sort, width, renderHeader, headerStyles }) => (
                                    <th
                                        style={{
                                            width,
                                            fontSize: '12px',
                                            verticalAlign: 'center',
                                            backgroundColor: PLATINUM,
                                            padding: '8px',
                                            borderBottom: `1px solid ${GAINSBORO}`,
                                            color: BLACK,
                                            ...headerStyles
                                        }}
                                        onClick={() => sort ? sortHandler(index) : {}}
                                    >
                                        { renderHeader || title }
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        {/* <tfoot>
                            <tr>{(footer || headers).map(renderFooter)}</tr>
                        </tfoot> */}
                        <tbody>
                            { map(data, ({ rowStyles = {}, ...row }, rowIndex) => (
                                <tr
                                    key={rowIndex}
                                    style={{
                                        ...tableModeStyles('tr'),
                                        ...rowStyles
                                    }}
                                >
                                    { map(columns, ({ render = () => null, child = () => null, index, columnStyle = {} }, colIndex) => (
                                        <td
                                            style={{
                                                // borderBottom: _drawRowBorderBottom(hasChildren, index + 1, size(children)),
                                                backgroundColor: WHITE,
                                                padding: '0',
                                                ...tableModeStyles('td', colIndex),
                                                ...columnStyle
                                            }}
                                        >
                                            <ColumnWrapper
                                                parent={render(row, props)}
                                                child={child(row, props)}
                                            />
                                        </td>
                                    )) }
                                </tr>
                            )) }
                        </tbody>
                    </table>
                }
            />
        </CardTableWrapper>
    );
};

BOTable.propTypes = {
    data: oneOfType([object, array]),
    columns: oneOfType([object, array]),
    tableStyles: object,
    mode: string,
    pagination: string
};

BOTable.defaultProps = {
    data: [],
    columns: [],
    tableStyles: {},
    mode: 'list',
    pagination: 'none'
};

export default BOTable;
